import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Link, Navigate, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { JsonLd } from '../../utils/structuredData';
import { trackEvent } from '../../utils/analytics';
import { useTheme } from '../../contexts/ThemeContext';
import { useInMarketingShell } from '../Marketing/MarketingContext';
import CCPlayerTheater from '../CCPlayer/CCPlayerTheater';
import { PlayIcon } from '../CCPlayer/icons';
import {
  SERIES, getLiveTutorial, upNext, liveInSeries, formatDuration,
} from './tutorialConfigs';
import TUTORIAL_TRANSCRIPTS from './tutorialTranscripts';
import {
  generateTutorialVideoSchema, generateTutorialBreadcrumbSchema,
} from './tutorialSchema';
import './Tutorials.css';

/**
 * TutorialVideoPage — /tutorials/<slug>/ (design doc §6.2; visual spec =
 * Agent Creative's Watch redesign, implemented pixel-by-pixel per Nitish's
 * ruling — sample source of truth in the linked design-review chat, d-watch/*).
 *
 * The four structural moves vs the first-ship layout:
 *  1. Up-next sits BESIDE the player at every width ≥1000px and immediately
 *     under it below that — never below the transcript (watch-rate lever).
 *  2. The answer block is the page's LEAD PARAGRAPH (largest body type),
 *     not a callout — still the AEO surface, still prerendered.
 *  3. The transcript keeps full DOM presence in a quieter voice; whole rows
 *     are the seek affordance.
 *  4. An ENDSCREEN owns the moment of maximum intent: when the video ends,
 *     the next video is one click, with a cancellable 8s auto-advance
 *     (disabled entirely under prefers-reduced-motion).
 *
 * Load-bearing rules preserved:
 *  - Route validates against LIVE entries only (D4); unknown slug → hub.
 *  - Watch-intent autoplay: navigation state { autoplay: true } from any
 *    card/up-next/endscreen; direct/SEO visits keep the poster.
 *  - answerBlock + transcript are real static page text (prerendered).
 *  - VideoObject + BreadcrumbList JSON-LD; series breadcrumb links the hub
 *    anchor (stable ids, R1).
 *  - The endscreen is an overlay OWNED BY THIS PAGE — CCPlayer stays generic
 *    (ended-detection via the shared videoRef, not a player fork).
 */
const AUTO_ADVANCE_SECONDS = 8;

const TutorialVideoPage = () => {
  const { tutorialSlug } = useParams();
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const wantAutoplay = Boolean(navState && navState.autoplay);
  const playerApi = useRef(null);
  const videoRef = useRef(null);
  const advanceTimer = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [ended, setEnded] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  // Theme contract (RoleLandingPage pattern) — see TutorialsHubPage.
  const inShell = useInMarketingShell();
  const { isDarkMode } = useTheme();
  const dark = inShell ? true : isDarkMode;

  const t = getLiveTutorial(tutorialSlug);

  const nextUp = t ? upNext(t.slug, 4) : [];
  const next = nextUp.length > 0 ? nextUp[0] : null;

  const goNext = useCallback(() => {
    if (!next) return;
    navigate(`/tutorials/${next.slug}/`, { state: { autoplay: true } });
  }, [navigate, next]);

  // Ended/replayed detection on the shared video element. No dependency
  // array: listeners re-attach every render, so a retry-recreated <video>
  // node can never be left unobserved.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onEnded = () => {
      setEnded(true);
      setCancelled(false);
      clearTimeout(advanceTimer.current);
      const reducedMotion = typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (next && !reducedMotion) {
        advanceTimer.current = setTimeout(() => {
          trackEvent('cta_click', {
            cta_name: 'tutorial_endscreen_auto', page: 'tutorials', location: 'endscreen', video: next.slug,
          });
          goNext();
        }, AUTO_ADVANCE_SECONDS * 1000);
      }
    };
    const onPlay = () => setEnded(false);
    v.addEventListener('ended', onEnded);
    v.addEventListener('play', onPlay);
    return () => {
      v.removeEventListener('ended', onEnded);
      v.removeEventListener('play', onPlay);
    };
  });

  // New slug = new page: reset endscreen state, kill any pending advance.
  useEffect(() => {
    setEnded(false);
    setCancelled(false);
    setCurrentTime(0);
    return () => clearTimeout(advanceTimer.current);
  }, [tutorialSlug]);

  if (!t) return <Navigate to="/tutorials" replace />;

  const series = SERIES.find((s) => s.id === t.series);
  const nextSeries = next ? SERIES.find((s) => s.id === next.series) : null;
  const transcript = TUTORIAL_TRANSCRIPTS[t.slug] || [];
  const seriesItems = liveInSeries(t.series);
  const pos = seriesItems.findIndex((x) => x.slug === t.slug);
  const pageUrl = `${SEO_DEFAULTS.url}/tutorials/${t.slug}/`;

  // Active row = the last transcript row whose timestamp has been reached.
  let activeIdx = -1;
  transcript.forEach((row, i) => { if (currentTime >= row.t) activeIdx = i; });

  const rowSeek = (row) => {
    if (playerApi.current) playerApi.current.seekTo(row.t, { andPlay: true });
    trackEvent('cta_click', {
      cta_name: 'tutorial_chip_seek', page: 'tutorials', location: 'transcript', video: t.slug,
    });
  };

  const replay = () => {
    clearTimeout(advanceTimer.current);
    setEnded(false);
    trackEvent('cta_click', {
      cta_name: 'tutorial_endscreen_replay', page: 'tutorials', location: 'endscreen', video: t.slug,
    });
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      v.play().catch(() => {});
    }
  };

  const cancelAdvance = () => {
    clearTimeout(advanceTimer.current);
    setCancelled(true);
    trackEvent('cta_click', {
      cta_name: 'tutorial_endscreen_cancel', page: 'tutorials', location: 'endscreen', video: t.slug,
    });
  };

  return (
    <div className={`tut-page tut-page--video ${dark ? 'dark' : 'light'}${inShell ? ' in-shell' : ''}`}>
      <Helmet>
        <title>{t.seoTitle}</title>
        <meta name="description" content={t.description} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={t.seoTitle} />
        <meta property="og:description" content={t.description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="video.other" />
        <meta property="og:image" content={`${SEO_DEFAULTS.url}${t.thumbnail}`} />
        {/* 'Inter Tight' display face — standard marketing font link. */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generateTutorialVideoSchema(t)} />
      <JsonLd data={generateTutorialBreadcrumbSchema(t)} />

      <div className="wch">
        {/* Breadcrumb + series progress */}
        <div className="wch-top">
          <nav className="wch-crumb" aria-label="Breadcrumb">
            <Link to="/tutorials/">Tutorials</Link>
            <span aria-hidden="true">/</span>
            <Link to={`/tutorials/#${t.series}`}>{series ? series.label : t.series}</Link>
            <span aria-hidden="true">/</span>
            <b aria-current="page">{t.title}</b>
          </nav>
          {seriesItems.length > 1 && (
            <div className="wch-prog" aria-label={`Video ${pos + 1} of ${seriesItems.length} in ${series ? series.label : t.series}`}>
              <span>{pos + 1} of {seriesItems.length} in {series ? series.label : t.series}</span>
              <span className="wch-prog__dots" aria-hidden="true">
                {seriesItems.map((x, i) => (
                  <span key={x.slug} className={`wch-prog__dot ${i <= pos ? 'is-on' : ''}`} />
                ))}
              </span>
            </div>
          )}
        </div>

        <div className="wch-grid">
          <main>
            {/* Player stage + endscreen overlay (page-owned; CCPlayer generic) */}
            <div className={`wch-stage ${ended ? 'is-ended' : ''}`}>
              <CCPlayerTheater
                src={t.mp4}
                poster={t.thumbnail}
                captions={t.vtt}
                title={t.title}
                theme={dark ? 'dark' : 'light'}
                autoPlay={wantAutoplay}
                apiRef={playerApi}
                videoRef={videoRef}
                onTimeUpdate={setCurrentTime}
                analyticsContext={{ video: t.slug, page: 'tutorials', location: 'watch' }}
              />
              {next && (
                <div className={`wch-end ${cancelled ? 'is-cancelled' : ''}`}>
                  <span className="wch-end__label">Up next</span>
                  <button type="button" className="wch-end__replay" onClick={replay}>Replay</button>
                  <div className="wch-end__card">
                    <span className="wch-end__thumb">
                      <img src={next.thumbnail} alt="" width="1280" height="720" />
                    </span>
                    <div>
                      <span className="wch-end__series">{nextSeries ? nextSeries.label : next.series}</span>
                      <h3 className="wch-end__title">{next.title}</h3>
                      <p className="wch-end__meta">{formatDuration(next.durationSeconds)} · Filmed in the real product</p>
                      <button
                        type="button"
                        className="wch-end__go"
                        onClick={() => {
                          clearTimeout(advanceTimer.current);
                          trackEvent('cta_click', {
                            cta_name: 'tutorial_endscreen_next', page: 'tutorials', location: 'endscreen', video: next.slug,
                          });
                          goNext();
                        }}
                      >
                        <span><PlayIcon size={15} /> Play next</span>
                      </button>
                      <button type="button" className="wch-end__cancel" onClick={cancelAdvance}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="wch-head">
              <h1 className="wch-title">{t.title}</h1>
              <div className="wch-meta">
                <Link to={`/tutorials/#${t.series}`} className="wch-tag">
                  {series ? series.label : t.series}
                </Link>
                <span className="wch-dot" aria-hidden="true">·</span>
                <span>{formatDuration(t.durationSeconds)}</span>
                <span className="wch-dot" aria-hidden="true">·</span>
                <span>Filmed in the real product</span>
              </div>
            </div>

            {/* answerBlock — the AEO surface as the page's LEAD paragraph. */}
            <p className="wch-answer">{t.answerBlock}</p>
            {t.guideHref && (
              <p className="wch-answer">
                <Link to={t.guideHref}>{t.guideLinkText}</Link>
              </p>
            )}
            <div className="wch-rule" aria-hidden="true" />

            {transcript.length > 0 && (
              <section className="wch-tr" aria-label="Transcript">
                <div className="wch-tr__head">
                  <h2 className="wch-tr__title">Transcript</h2>
                  <span className="wch-tr__note">Click any line to jump</span>
                </div>
                <ol className="wch-tr__list">
                  {transcript.map((row, i) => (
                    <li key={row.t}>
                      <button
                        type="button"
                        className={`wch-tr__row ${i === activeIdx ? 'is-active' : ''}`}
                        onClick={() => rowSeek(row)}
                        aria-label={`Jump to ${formatDuration(row.t)}`}
                      >
                        <span className="wch-tr__t">{formatDuration(row.t)}</span>
                        <span className="wch-tr__x">{row.text}</span>
                      </button>
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </main>

          <aside className="wch-rail">
            {nextUp.length > 0 && (
              <section aria-label="Up next">
                <h2 className="wch-rail__title">Up next</h2>
                <div className="wch-next">
                  {nextUp.map((n, i) => {
                    const nSeries = SERIES.find((s) => s.id === n.series);
                    return (
                      <Link
                        key={n.slug}
                        to={`/tutorials/${n.slug}/`}
                        state={{ autoplay: true }}
                        className={`wch-next__item ${i === 0 ? 'is-first' : ''}`}
                        onClick={() => trackEvent('cta_click', {
                          cta_name: 'tutorial_upnext', page: 'tutorials', location: 'rail', video: n.slug,
                        })}
                      >
                        <span className="wch-next__fr">
                          <img src={n.thumbnail} alt="" loading="lazy" width="1280" height="720" />
                          <span className="cin-dur wch-next__dur">{formatDuration(n.durationSeconds)}</span>
                        </span>
                        <span>
                          <span className="wch-next__s">
                            {i === 0 && <span className="wch-next__badge">Next · </span>}
                            {nSeries ? nSeries.label : n.series}
                          </span>
                          <span className="wch-next__n">{n.title}</span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {seriesItems.length > 1 && (
              <section className="wch-idx" aria-label="Series index">
                <h2 className="wch-rail__title">{series ? series.label : t.series}</h2>
                {seriesItems.map((x, i) => (
                  <Link
                    key={x.slug}
                    to={`/tutorials/${x.slug}/`}
                    state={{ autoplay: true }}
                    className={`wch-idx__row ${x.slug === t.slug ? 'is-current' : ''}`}
                    onClick={() => trackEvent('cta_click', {
                      cta_name: 'tutorial_upnext', page: 'tutorials', location: 'series-index', video: x.slug,
                    })}
                  >
                    <span>{i + 1}. {x.title}</span>
                    <span className="wch-idx__n">{formatDuration(x.durationSeconds)}</span>
                  </Link>
                ))}
              </section>
            )}

            {/* CTA card — logged-OUT acquisition only (canon copy incl. the
                200-credits line, Rhea's ruling). */}
            {inShell && (
              <aside className="wch-cta">
                <h2 className="wch-cta__t">Ready to hire yours?</h2>
                <p className="wch-cta__s">
                  Try it free with 200 starting credits, no credit card needed. Your first
                  employee is about a minute of setup.
                </p>
                <button
                  type="button"
                  className="cin-cta"
                  onClick={() => {
                    trackEvent('cta_click', { cta_name: 'tutorial_cta_signup', page: 'tutorials', location: 'video-cta-card', video: t.slug });
                    navigate('/signup');
                  }}
                >
                  Hire your first AI employee
                </button>
              </aside>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default TutorialVideoPage;
